import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { ReadOnlyArea, ReadOnlyBanner } from "@/components/ReadOnly";
import { useAuth } from "@/contexts/AuthContext";
import { shortDay, todayInZurich, useFormat } from "@/lib/format";
import { fetchPieces, fetchReturns, type StockPiece } from "@/services/stock";
import {
  fetchUpcomingContracts, fetchReservations, createReservation, updateReservation, reservationPrepared,
  cancelReservation, type Reservation, type UpcomingContract,
} from "@/services/reservations";

// Z Furniture · Commercial — "les commerciaux réservent des meubles du stock pour un contrat".
// His three steps — Contract → Furniture → Send — then his reservation lists: mine, and for a
// manager everyone's. Reserving marks the pieces at once; they leave stock only when prepared.

type Contract = { client: string; contractId: number | null; deliv: string | null; time: string | null; type: string | null; wh: number | null };
type Group = { key: string; list: StockPiece[]; first: StockPiece; cat: string };
const COND_ORDER: Record<string, number> = { new: 0, good: 1, light_marks: 2 };
// His `norm`: accents and case do not matter in a search.
const fold = (s: string | null | undefined) => (s ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function CommercialPage({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const { staff } = useAuth();
  const manager = !!staff?.is_admin;
  const [tab, setTab] = useState<"new" | "mine" | "all">("new");
  const mine = useQuery({ queryKey: ["reservations", "mine"], queryFn: () => fetchReservations({ mine: true }), refetchInterval: 15000 });
  const openMine = (mine.data?.reservations ?? []).filter((r) => r.status === "sent").length;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist">
        {(["new", "mine", ...(manager ? ["all" as const] : [])] as const).map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === k ? "bg-orange-600 text-white" : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"}`}>
            {t(`com.tab.${k}`)}{k === "mine" && openMine > 0 && <span className="ml-1.5 rounded-full bg-white/25 px-1.5 text-xs">{openMine}</span>}
          </button>
        ))}
      </div>
      {/* His banner and greyed buttons for a read-only person; the lists stay readable. */}
      <ReadOnlyBanner module="commercial" text={t("com.readOnly")} />
      {tab === "new" && <ReadOnlyArea module="commercial"><NewReservation warehouseId={warehouseId} onSeeMine={() => setTab("mine")} /></ReadOnlyArea>}
      {tab === "mine" && <ReservationList mine manager={manager} onNew={() => setTab("new")} />}
      {tab === "all" && manager && <ReservationList manager onNew={() => setTab("new")} />}
    </div>
  );
}

// ---- new reservation -----------------------------------------------------------------------------

function NewReservation({ warehouseId, onSeeMine }: { warehouseId: number | null; onSeeMine: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [step, setStep] = useState(1);
  const [contract, setContract] = useState<Contract | null>(null);
  const [wh, setWh] = useState<number | null>(warehouseId);
  // piece group key → how many of it
  const [cart, setCart] = useState<Map<string, number>>(new Map());
  const [prep, setPrep] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState<{ client: string; n: number; prep: string; ref: string } | null>(null);
  const pieces = useQuery({ queryKey: ["stock-pieces", wh, "in_stock"], queryFn: () => fetchPieces(wh, "in_stock"), enabled: !!wh, refetchInterval: 15000 });
  const groups = useGroups(pieces.data?.pieces ?? []);
  const units = [...cart].flatMap(([k, q]) => (groups.find((g) => g.key === k)?.list ?? []).slice(0, q));
  const count = units.length;

  const chooseContract = (c: Contract) => { setContract(c); setWh(c.wh ?? warehouseId); setCart(new Map()); setPrep(""); setErr(""); setStep(2); };
  const add = (g: Group, d: number) => setCart((m) => { const n = new Map(m); const q = Math.max(0, Math.min(g.list.length, (n.get(g.key) ?? 0) + d)); if (q) n.set(g.key, q); else n.delete(g.key); return n; });

  const send = useMutation({
    mutationFn: () => createReservation({
      stock_piece_ids: units.map((u) => u.id), warehouse_id: wh, client_warehouse_id: contract?.wh, contract_id: contract?.contractId,
      client_name: contract?.client, deliver_on: contract?.deliv, deliver_at: contract?.time, delivery_type: contract?.type,
      prepare_on: prep || defaultPrep(contract?.deliv ?? null), note: note.trim(),
    }),
    onSuccess: (r) => {
      setDone({ client: r.client_name || contract?.client || "—", n: r.pieces.length, prep: r.prepare_on ?? "", ref: r.reference });
      setContract(null); setCart(new Map()); setNote(""); setPrep(""); setStep(1);
      void qc.invalidateQueries({ queryKey: ["reservations"] });
      void qc.invalidateQueries({ queryKey: ["stock-pieces"] });
    },
    // His rule when a piece was just taken by someone else: the list is refreshed and the
    // selection shrinks to what is still free.
    onError: () => { setErr(t("com.justTaken")); void qc.invalidateQueries({ queryKey: ["stock-pieces"] }); },
  });
  useEffect(() => {
    if (!pieces.data) return;
    setCart((m) => { const n = new Map<string, number>(); for (const [k, q] of m) { const g = groups.find((x) => x.key === k); if (g) n.set(k, Math.min(q, g.list.length)); } return n; });
  }, [pieces.data]); // eslint-disable-line react-hooks/exhaustive-deps

  if (done) {
    return (
      <section className="mx-auto max-w-xl rounded-xl bg-white p-6 text-center shadow-sm ring-1 ring-slate-200">
        <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-emerald-100 text-3xl text-emerald-700" aria-hidden="true">✓</div>
        <h2 className="text-lg font-semibold text-slate-900">{t("com.doneTitle", { client: done.client })}</h2>
        <p className="mt-2 text-sm text-slate-600">{t(done.n === 1 ? "com.doneText1" : "com.doneTextN", { n: done.n, prep: shortDay(done.prep) })}</p>
        <p className="mt-2 text-xs text-slate-400">{t("com.reference", { ref: done.ref })}</p>
        <div className="mt-4 flex justify-center gap-2">
          <button onClick={onSeeMine} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("com.seeMine")}</button>
          <button onClick={() => setDone(null)} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white">{t("com.tab.new")}</button>
        </div>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {/* His step bar: done steps show ✓ and take you back; later ones wait. */}
      <ol className="flex gap-2">
        {[1, 2, 3].map((n) => (
          <li key={n}>
            <button disabled={n > step} onClick={() => setStep(n)}
              className={`flex items-center gap-2 rounded-full px-3 py-1 text-sm ${step === n ? "bg-slate-900 text-white" : n < step ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200" : "bg-white text-slate-400 ring-1 ring-slate-200"}`}>
              <b>{n < step ? "✓" : n}</b>{t(`com.step${n}`)}
            </button>
          </li>
        ))}
      </ol>
      {step === 1 && <ContractStep onPick={chooseContract} />}
      {step > 1 && contract && (
        <div className="flex items-center gap-3 rounded-lg bg-orange-50 px-3 py-2 ring-1 ring-orange-300">
          <span className="min-w-0 flex-1 text-sm"><b>{contract.client}</b>
            <span className="text-slate-600"> · {[contract.contractId && t("com.contractN", { n: contract.contractId }), contract.deliv ? t("com.delivOn", { date: dayAndTime(contract.deliv, contract.time) }) : t("com.delivToPlan")].filter(Boolean).join(" · ")}</span></span>
          <button onClick={() => setStep(1)} className="text-sm underline">{t("com.change")}</button>
        </div>
      )}
      {step === 2 && contract && (
        <FurnitureStep contract={contract} wh={wh} onWh={(w) => { setWh(w); setCart(new Map()); }} groups={groups} loading={pieces.isLoading}
          cart={cart} add={add} count={count} err={err} onContinue={() => { if (!count) { setErr(t("com.addOne")); return; } setErr(""); setStep(3); }} />
      )}
      {step === 3 && contract && (
        <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
          <h2 className="font-medium text-slate-900">{t("com.s3")}</h2>
          <ul className="mt-2 divide-y divide-slate-100">
            {[...cart].map(([k, q]) => {
              const g = groups.find((x) => x.key === k);
              if (!g) return null;
              return (
                <li key={k} className="flex items-center gap-3 py-2">
                  <Thumb p={g.list.find((x) => x.photo) ?? g.first} className="h-12 w-12" />
                  <span className="min-w-0 flex-1 text-sm"><b>{q} × {g.first.product?.name || `#${g.first.myotaku_product_id}`}</b>
                    <span className="block text-xs text-slate-500">{t(`stock.cond.${g.first.condition}`)} · {g.first.warehouse_name}</span></span>
                  <Stepper q={q} max={g.list.length} onChange={(d) => add(g, d)} />
                </li>
              );
            })}
          </ul>
          <button onClick={() => setStep(2)} className="mt-1 text-sm text-slate-600 underline">+ {t("com.addOthers")}</button>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-500">{t("com.prepare")}
              <input type="date" value={prep || defaultPrep(contract.deliv)} onChange={(e) => setPrep(e.target.value)} min={todayInZurich()} max={contract.deliv ?? undefined}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" />
              <span className="mt-1 block">{contract.deliv ? t("com.prepareHintDay", { date: shortDay(contract.deliv) }) : t("com.prepareNoDeliv")}</span>
            </label>
            <label className="text-xs text-slate-500">{t("com.note")} <span className="text-slate-400">· {t("stock.optional")}</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={t("com.notePh")}
                className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" />
            </label>
          </div>
          {err && <p role="alert" className="mt-2 text-sm text-red-600">{err}</p>}
          <button disabled={send.isPending || count === 0} onClick={() => send.mutate()}
            className="mt-3 w-full rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40">
            {send.isPending ? t("com.sending") : t(count === 1 ? "com.send1" : "com.sendN", { n: count })}
          </button>
          <p className="mt-2 text-xs text-slate-500">{t("com.sendHint")}</p>
        </section>
      )}
    </div>
  );
}

// His contract step: contracts with a delivery coming, every typed word over name, number, client
// and rep, ignoring accents; the signed-in rep's own first; "Continue with « … »" when nothing
// matches; the contract typed by hand.
function ContractStep({ onPick }: { onPick: (c: Contract) => void }) {
  const { t } = useT();
  const { staff } = useAuth();
  const contracts = useQuery({ queryKey: ["upcoming-contracts"], queryFn: fetchUpcomingContracts });
  const [q, setQ] = useState("");
  const [manual, setManual] = useState<{ open: boolean; client: string; cid: string; deliv: string; wh: number | null }>({ open: false, client: "", cid: "", deliv: "", wh: null });
  const [err, setErr] = useState("");
  const today = todayInZurich();
  const me = fold(staff?.name);
  const all = (contracts.data?.contracts ?? []).filter((c) => !c.starts_at || c.starts_at.slice(0, 10) >= today);
  const words = fold(q).trim().split(/\s+/).filter(Boolean);
  const hay = (c: UpcomingContract) => fold(`${c.contract_name ?? ""} ${c.contract_id} ${c.client_name ?? ""} ${(c.sales_reps ?? []).map((r) => r.name).join(" ")}`);
  const isMine = (c: UpcomingContract) => !!me && (c.sales_reps ?? []).some((r) => fold(r.name).includes(me));
  const rows = all.filter((c) => words.every((w) => hay(c).includes(w)))
    .sort((a, b) => Number(isMine(b)) - Number(isMine(a)) || (a.starts_at ?? "9").localeCompare(b.starts_at ?? "9"))
    .slice(0, words.length ? 40 : 25);
  const returns = useQuery({ queryKey: ["stock-returns", null], queryFn: () => fetchReturns(null) });
  const toContract = (c: UpcomingContract): Contract => ({ client: c.contract_name || c.client_name || `#${c.contract_id}`, contractId: c.contract_id,
    deliv: c.starts_at?.slice(0, 10) ?? null, time: c.starts_at?.slice(11, 16) ?? null, type: c.type_label, wh: c.warehouse?.id ?? null });
  const continueWithQuery = () => {
    const v = q.trim();
    setManual((m) => ({ ...m, open: true, ...( /^\d{3,7}$/.test(v) ? { cid: v } : { client: v }) }));
  };
  const submitManual = () => {
    if (!manual.client.trim()) { setErr(t("com.manClientReq")); return; }
    if (manual.cid && !/^\d{3,7}$/.test(manual.cid)) { setErr(t("com.manCidBad")); return; }
    if (manual.deliv && manual.deliv < today) { setErr(t("com.manDelivPast")); return; }
    onPick({ client: manual.client.trim(), contractId: manual.cid ? Number(manual.cid) : null, deliv: manual.deliv || null, time: null, type: null, wh: manual.wh });
  };
  return (
    <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <h2 className="font-medium text-slate-900">{t("com.whichClient")}</h2>
      <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("com.searchContract")} autoComplete="off"
        className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-base" />
      {q.trim() && rows.length === 0 && (
        <button onClick={continueWithQuery} className="mt-2 flex w-full items-center gap-3 rounded-lg bg-orange-50 px-3 py-2 text-left ring-1 ring-orange-300">
          <span className="min-w-0 flex-1"><b className="block text-sm">{t("com.continueWith", { q: q.trim() })}</b><span className="text-xs text-slate-500">{t("com.notFoundManual")}</span></span>
          <span aria-hidden="true">›</span>
        </button>
      )}
      {contracts.error && <p className="mt-2 text-sm text-red-600">{(contracts.error as Error).message}</p>}
      <ul className="mt-2 max-h-[28rem] divide-y divide-slate-100 overflow-y-auto">
        {rows.map((c) => (
          <li key={c.contract_id}>
            <button onClick={() => onPick(toContract(c))} className="flex w-full items-center gap-3 py-2 text-left hover:bg-slate-50">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-900">{c.contract_name || c.client_name || t("com.contractN", { n: c.contract_id })}</span>
                <span className="block truncate text-xs text-slate-500">{[c.client_name !== c.contract_name && c.client_name, t("com.contractN", { n: c.contract_id }),
                  c.starts_at ? t("com.delivOn", { date: dayAndTime(c.starts_at.slice(0, 10), c.starts_at.slice(11, 16)) }) : t("com.delivToPlan"), c.warehouse?.name].filter(Boolean).join(" · ")}
                  {c.sales_reps?.length ? <b className="text-slate-700"> · {c.sales_reps.map((r) => r.name || r.email).join(", ")}</b> : null}</span>
              </span>
              <span aria-hidden="true" className="text-slate-400">›</span>
            </button>
          </li>
        ))}
      </ul>
      {!contracts.isLoading && all.length === 0 && <p className="mt-2 text-sm text-slate-500">{t("com.noUpcoming")}</p>}
      {contracts.data?.reaches && <p className="mt-2 text-xs text-slate-400">{t("com.reaches", { d: shortDay(contracts.data.reaches) })}</p>}
      <details open={manual.open} onToggle={(e) => setManual((m) => ({ ...m, open: (e.target as HTMLDetailsElement).open }))} className="mt-3">
        <summary className="cursor-pointer text-sm text-slate-700">{t("com.notInList")}</summary>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-slate-500">{t("cartx.client")}<input value={manual.client} onChange={(e) => setManual((m) => ({ ...m, client: e.target.value }))} placeholder={t("com.clientPh")} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" /></label>
          <label className="text-xs text-slate-500">{t("com.contractNo")} <span className="text-slate-400">· {t("com.ifYouHave")}</span><input value={manual.cid} onChange={(e) => setManual((m) => ({ ...m, cid: e.target.value }))} inputMode="numeric" placeholder="27105" className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" /></label>
          <label className="text-xs text-slate-500">{t("com.delivDate")} <span className="text-slate-400">· {t("com.ifKnown")}</span><input type="date" min={today} value={manual.deliv} onChange={(e) => setManual((m) => ({ ...m, deliv: e.target.value }))} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-slate-900" /></label>
          <div className="text-xs text-slate-500">{t("cartx.warehouse")}
            <div className="mt-1 flex flex-wrap gap-1">
              {(returns.data?.warehouses ?? []).map((w) => (
                <button key={w.id} onClick={() => setManual((m) => ({ ...m, wh: w.id }))} aria-pressed={manual.wh === w.id}
                  className={`rounded-full px-3 py-1 text-sm ring-1 ${manual.wh === w.id ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-300"}`}>{w.name}</button>
              ))}
            </div>
          </div>
        </div>
        {err && <p role="alert" className="mt-2 text-sm text-red-600">{err}</p>}
        <button onClick={submitManual} className="mt-2 rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white">{t("com.continueClient")} ›</button>
      </details>
    </section>
  );
}

// His furniture step: the stock warehouse (the contract's by default; another means a transfer),
// a search, category chips, one card per product and condition with how many are free.
function FurnitureStep({ contract, wh, onWh, groups, loading, cart, add, count, err, onContinue }: {
  contract: Contract; wh: number | null; onWh: (w: number) => void; groups: Group[]; loading: boolean;
  cart: Map<string, number>; add: (g: Group, d: number) => void; count: number; err: string; onContinue: () => void;
}) {
  const { t } = useT();
  const returns = useQuery({ queryKey: ["stock-returns", null], queryFn: () => fetchReturns(null) });
  const warehouses = returns.data?.warehouses ?? [];
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sheet, setSheet] = useState<Group | null>(null);
  const cats = new Map<string, number>();
  for (const g of groups) cats.set(g.cat, (cats.get(g.cat) ?? 0) + g.list.length);
  const words = fold(q).trim().split(/\s+/).filter(Boolean);
  const rows = groups.filter((g) => cat === "all" || g.cat === cat)
    .filter((g) => words.every((w) => fold(`${g.first.product?.name ?? ""} ${g.cat} ${g.first.myotaku_product_id ?? ""} ${g.first.product?.sku ?? ""} ${t(`stock.cond.${g.first.condition}`)}`).includes(w)));
  const whName = (id: number | null) => warehouses.find((w) => w.id === id)?.name ?? "";
  return (
    <>
      <section className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1">
            {warehouses.map((w) => (
              <button key={w.id} onClick={() => onWh(w.id)} aria-pressed={wh === w.id}
                className={`rounded-full px-3 py-1 text-sm ring-1 ${wh === w.id ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-300"}`}>{w.name}</button>
            ))}
          </div>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("com.searchFurniture")} className="min-w-[200px] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        </div>
        {contract.wh && wh && contract.wh !== wh && <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{t("com.transferWarn", { from: whName(contract.wh), other: whName(wh) })}</p>}
        <div className="mt-3 flex flex-wrap gap-1">
          <button onClick={() => setCat("all")} aria-pressed={cat === "all"} className={chipCls(cat === "all")}>{t("com.allCats")} <span className="opacity-70">{[...cats.values()].reduce((a, b) => a + b, 0)}</span></button>
          {[...cats].sort((a, b) => a[0].localeCompare(b[0])).map(([c, n]) => (
            <button key={c} onClick={() => setCat(c)} aria-pressed={cat === c} className={chipCls(cat === c)}>{c} <span className="opacity-70">{n}</span></button>
          ))}
        </div>
        {!wh ? <p className="mt-3 text-sm text-slate-500">{t("stock.pickWarehouse")}</p>
          : loading ? <p className="mt-3 text-sm text-slate-400">{t("com.loadingStock")}</p>
          : rows.length === 0 ? <p className="mt-3 rounded-lg bg-slate-50 px-4 py-5 text-sm text-slate-500"><b className="block text-slate-700">{cat !== "all" || words.length ? t("com.noneFilter") : t("com.noneAt", { wh: whName(wh) })}</b>{t("com.noneHint")}</p>
          : (
            <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {rows.map((g) => {
                const q2 = cart.get(g.key) ?? 0;
                const damaged = g.list.some((p) => p.has_damage_photos || p.damage_note);
                return (
                  <li key={g.key} className={`flex flex-col rounded-lg p-2 ring-1 ${q2 ? "bg-orange-50 ring-orange-400" : "bg-white ring-slate-200"}`}>
                    <button onClick={() => setSheet(g)} className="relative" aria-label={t("com.seePhotos", { name: g.first.product?.name ?? "" })}>
                      <Thumb p={g.list.find((x) => x.photo) ?? g.first} className="h-28 w-full" />
                      {damaged && <span className="absolute bottom-1 left-1 rounded bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-900">{t("com.defectPhotos")}</span>}
                    </button>
                    <span className="mt-2 line-clamp-2 text-sm font-medium text-slate-900">{g.first.product?.name || `#${g.first.myotaku_product_id}`}</span>
                    <span className="mt-1 flex items-center gap-2 text-xs"><span className={condTone(g.first.condition)}>{t(`stock.cond.${g.first.condition}`)}</span><span className="text-slate-500">{t("com.nAvailable", { n: g.list.length })}</span></span>
                    <div className="mt-auto pt-2">
                      {q2 ? <Stepper q={q2} max={g.list.length} onChange={(d) => add(g, d)} />
                        : <button onClick={() => add(g, 1)} className="w-full rounded-lg bg-white py-1.5 text-sm ring-1 ring-slate-300">{t("com.add")}</button>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
      </section>
      <div className="sticky bottom-3 flex items-center gap-3 rounded-xl bg-slate-900 px-4 py-3 text-white shadow-lg">
        <span className="flex-1 text-sm">🛒 <b>{count}</b> {t(count === 1 ? "com.selected1" : "com.selectedN")}</span>
        {err && <span className="text-sm text-red-300">{err}</span>}
        <button onClick={onContinue} className="rounded-lg bg-orange-600 px-4 py-1.5 text-sm font-semibold">{t("com.continue")} ›</button>
      </div>
      {sheet && <UnitSheet g={sheet} taken={cart.get(sheet.key) ?? 0} onClose={() => setSheet(null)} />}
    </>
  );
}

// His unit photo sheet: one block per unit, in the order they would be taken.
function UnitSheet({ g, taken, onClose }: { g: Group; taken: number; onClose: () => void }) {
  const { t } = useT();
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-scrim/40 p-4" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-xl bg-white p-4 shadow-lg">
        <div className="flex items-start justify-between gap-3">
          <div><h3 className="font-semibold text-slate-900">{g.first.product?.name}</h3>
            <p className="text-xs text-slate-500">{g.cat} · {t(`stock.cond.${g.first.condition}`)} · {t("com.nAvailable", { n: g.list.length })}</p></div>
          <button onClick={onClose} className="rounded-lg px-2 py-1 text-sm ring-1 ring-slate-300">✕</button>
        </div>
        <ul className="mt-3 space-y-3">
          {g.list.map((p, i) => (
            <li key={p.id} className={`rounded-lg p-2 ring-1 ${i < taken ? "bg-orange-50 ring-orange-300" : "ring-slate-200"}`}>
              <p className="mb-1 text-xs font-semibold text-slate-600">{t("com.nthTaken", { n: i + 1 })} · <span className="font-mono">{p.reference}</span></p>
              <Thumb p={p} className="h-40 w-full" />
              <p className={`mt-1 text-xs ${p.damage_note ? "text-amber-800" : "text-slate-500"}`}>{p.damage_note ? `⚠️ ${p.damage_note}` : t("com.noDefectNoted")}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---- reservation lists ---------------------------------------------------------------------------

function ReservationList({ mine = false, manager, onNew }: { mine?: boolean; manager: boolean; onNew: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"sent" | "prepared" | "cancelled" | "all">("sent");
  const list = useQuery({
    queryKey: ["reservations", mine ? "mine" : `all-${filter}`],
    queryFn: () => fetchReservations(mine ? { mine: true } : { status: filter === "all" ? undefined : filter }),
    refetchInterval: 15000,
  });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["reservations"] }); void qc.invalidateQueries({ queryKey: ["stock-pieces"] }); };
  const prepared = useMutation({ mutationFn: reservationPrepared, onSuccess: refresh });
  const cancel = useMutation({ mutationFn: cancelReservation, onSuccess: refresh });
  const setEvent = useMutation({ mutationFn: ({ id, ev }: { id: number; ev: string }) => updateReservation(id, { myotaku_event_id: ev }), onSuccess: refresh });
  const [confirm, setConfirm] = useState<number | null>(null);
  const failed = (prepared.error || cancel.error || setEvent.error) as Error | null;
  const rows = list.data?.reservations ?? [];
  return (
    <div className="space-y-3">
      {!mine && (
        <div className="flex flex-wrap gap-1">
          {(["sent", "prepared", "cancelled", "all"] as const).map((f) => <button key={f} onClick={() => setFilter(f)} aria-pressed={filter === f} className={chipCls(filter === f)}>{t(`com.filter.${f}`)}</button>)}
        </div>
      )}
      {failed && <p className="text-sm text-red-600">{failed.message}</p>}
      {list.isLoading && <p className="text-sm text-slate-400">…</p>}
      {!list.isLoading && rows.length === 0 && (
        <div className="rounded-xl bg-white px-5 py-6 text-center text-sm text-slate-500 ring-1 ring-slate-200">
          <b className="block text-slate-700">{t("com.noneYet")}</b>{t("com.noneYetHint")}
          <div className="mt-3"><button onClick={onNew} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white">{t("com.tab.new")}</button></div>
        </div>
      )}
      {rows.map((r) => (
        <ReservationCard key={r.id} r={r} manager={manager} confirming={confirm === r.id} onConfirm={(on) => setConfirm(on ? r.id : null)}
          onCancel={() => cancel.mutate(r.id)} onPrepared={() => prepared.mutate(r.id)} onEvent={(ev) => setEvent.mutate({ id: r.id, ev })} />
      ))}
    </div>
  );
}

function ReservationCard({ r, manager, confirming, onConfirm, onCancel, onPrepared, onEvent }: {
  r: Reservation; manager: boolean; confirming: boolean; onConfirm: (on: boolean) => void; onCancel: () => void; onPrepared: () => void; onEvent: (ev: string) => void;
}) {
  const { t } = useT();
  const { dateTime } = useFormat();
  const open = r.status === "sent";
  // His statuses: cancelled, prepared, in the planning · #event, sent · logistics notified.
  const [status, tone] = r.status === "cancelled" ? [t("com.status.cancelled"), "bg-slate-100 text-slate-500"]
    : r.status === "prepared" ? [t("com.status.prepared"), "bg-emerald-100 text-emerald-800"]
    : r.myotaku_event_id ? [t("com.status.planned", { ev: r.myotaku_event_id }), "bg-emerald-100 text-emerald-800"]
    : [t("com.status.sent"), "bg-amber-100 text-amber-900"];
  return (
    <article className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <b className="text-slate-900">{r.client_name || "—"}</b>
          <p className="text-xs text-slate-500">{[r.myotaku_contract_id && t("com.contractN", { n: r.myotaku_contract_id }),
            r.deliver_on ? t("com.delivOn", { date: shortDay(r.deliver_on) }) : t("com.delivToPlan"),
            t("com.prepOn", { date: shortDay(r.prepare_on) })].filter(Boolean).join(" · ")}</p>
        </div>
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${tone}`}>{status}</span>
      </div>
      <ul className="mt-2 flex flex-wrap gap-2">
        {r.pieces.map((p) => (
          <li key={p.id} className="flex items-center gap-2 rounded-lg bg-slate-50 py-1 pl-1 pr-2">
            {p.photo?.thumb_url || p.photo?.url || p.product?.image_url
              ? <img src={p.photo?.thumb_url || p.photo?.url || p.product?.image_url || ""} alt="" className="h-10 w-10 rounded object-cover" />
              : <span className="h-10 w-10 rounded bg-slate-200" />}
            <span className="text-xs"><b className="block text-slate-900">{p.product?.name || `#${p.myotaku_product_id}`}</b>
              <span className="text-slate-500">{manager
                ? [p.reference, [p.pallet && t("stock.palletN", { p: p.pallet }), p.rack].filter(Boolean).join(" · "), p.unit_count && p.unit_count > 1 ? `${p.unit_number}/${p.unit_count}` : null].filter(Boolean).join(" · ")
                : t(`stock.cond.${p.condition}`)}</span></span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-500">{[manager && r.requested_by, t("com.reservedAt", { when: dateTime(r.created_at) }), t("com.reference", { ref: r.reference }), r.note && `« ${r.note} »`].filter(Boolean).join(" · ")}</p>
      {/* His `admin && al.err`: a manager sees when the Slack notice did not reach logistics. */}
      {manager && [r.alert, r.cancel_notice].map((n, i) => n?.state === "failed" &&
        <p key={i} className="mt-1 text-xs text-red-700">{t("com.noticeFailed", { error: n.error || "?" })}</p>)}
      {open && (
        <ReadOnlyArea module="commercial">{confirming ? (
          <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-900">
            <p>{t("com.cancelWarn")}{r.myotaku_event_id ? ` ${t("com.cancelWarnPlanning")}` : ""}</p>
            <div className="mt-2 flex gap-2"><button onClick={onCancel} className="rounded-lg bg-red-700 px-3 py-1 text-white">{t("com.cancelSure")}</button><button onClick={() => onConfirm(false)} className="rounded-lg px-3 py-1 ring-1 ring-red-200">{t("unit.no")}</button></div>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {manager && <button onClick={onPrepared} className="rounded-lg bg-emerald-600 px-3 py-1 text-sm text-white">{t("com.markPrepared")}</button>}
            <button onClick={() => onConfirm(true)} className="rounded-lg px-3 py-1 text-sm ring-1 ring-slate-300">{t("com.cancelResa")}</button>
            {manager && (
              <form onSubmit={(e) => { e.preventDefault(); onEvent(new FormData(e.currentTarget).get("ev") as string); }} className="flex items-center gap-1">
                <input name="ev" defaultValue={r.myotaku_event_id ?? ""} placeholder={t("com.eventPh")} inputMode="numeric" className="w-28 rounded border border-slate-300 px-2 py-1 text-xs" />
                <button className="rounded px-2 py-1 text-xs ring-1 ring-slate-300">{t("com.saveEvent")}</button>
              </form>
            )}
          </div>
        )}</ReadOnlyArea>
      )}
    </article>
  );
}

// ---- small pieces --------------------------------------------------------------------------------

// Pieces of one product in one condition are interchangeable: one card, the oldest taken first.
// His `avail`: identified and not damaged — a damaged piece is never offered to a client.
function useGroups(pieces: StockPiece[]): Group[] {
  return useMemo(() => {
    const m = new Map<string, StockPiece[]>();
    pieces.filter((p) => p.status === "in_stock" && p.condition !== "damaged" && (p.myotaku_product_id != null || !!p.product?.name)).forEach((p) => {
      const k = `${p.myotaku_product_id ?? p.product?.name}|${p.condition}`;
      m.set(k, [...(m.get(k) ?? []), p]);
    });
    return [...m].map(([key, list]) => {
      const sorted = [...list].sort((a, b) => String(a.received_at).localeCompare(String(b.received_at)));
      return { key, list: sorted, first: sorted[0], cat: sorted[0].product?.type?.name || "—" };
    }).sort((a, b) => a.cat.localeCompare(b.cat) || String(a.first.product?.name).localeCompare(String(b.first.product?.name))
      || (COND_ORDER[a.first.condition] ?? 3) - (COND_ORDER[b.first.condition] ?? 3));
  }, [pieces]);
}

function Thumb({ p, className }: { p: StockPiece; className: string }) {
  const src = p.photo?.thumb_url || p.photo?.url || p.product?.image_url;
  return src ? <img src={src} alt="" className={`${className} rounded bg-white object-contain`} /> : <span className={`${className} grid place-items-center rounded bg-slate-100 text-2xl`} aria-hidden="true">🪑</span>;
}

function Stepper({ q, max, onChange }: { q: number; max: number; onChange: (d: number) => void }) {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-2">
      <button onClick={() => onChange(-1)} aria-label={t("com.removeOne")} className="h-7 w-7 rounded ring-1 ring-slate-300">−</button>
      <span className="w-5 text-center text-sm tabular-nums">{q}</span>
      <button onClick={() => onChange(1)} disabled={q >= max} aria-label={t("com.addOne1")} className="h-7 w-7 rounded ring-1 ring-slate-300 disabled:opacity-30">+</button>
    </span>
  );
}

const chipCls = (on: boolean) => `rounded-full px-3 py-1 text-sm ring-1 ${on ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-700 ring-slate-300"}`;
const condTone = (c: string) => `rounded px-1.5 py-0.5 font-medium ${c === "light_marks" ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`;

// His default preparation day: the working day before the delivery (a Monday delivery is prepared
// on Friday), never a day already gone; with no delivery, the next working day.
function defaultPrep(deliv: string | null) {
  const today = todayInZurich();
  const step = (iso: string, d: number) => {
    const x = new Date(`${iso}T12:00:00`);
    do { x.setDate(x.getDate() + d); } while (x.getDay() === 0 || x.getDay() === 6);
    return x.toISOString().slice(0, 10);
  };
  if (!deliv) return step(today, 1);
  const day = step(deliv, -1);
  return day < today ? today : day;
}

function dayAndTime(day: string, time: string | null) {
  return `${shortDay(day)}${time && time !== "00:00" ? ` · ${time.replace(":", "h")}` : ""}`;
}
